import React, { useMemo } from "react";
import { IonItem } from "@ionic/react";
import classNames from "classnames";
import { ProgramData, ProgramRole } from "@interfaces/Interfaces";
import { Icon_Radio_Active, Icon_Radio_Grey } from "@assets/images";
import * as ProgramUtils from "@utils/programUtils";

interface AreaTeamSelectionProps {
  program: ProgramData;
  selectedArea: string;
  selectedTeam: string;
  selectedPrimaryRoles: string[];
  onAreaChange: (area: string) => void;
  onTeamChange: (team: string) => void;
}

const AreaTeamSelection: React.FC<AreaTeamSelectionProps> = ({
  program,
  selectedArea,
  selectedTeam,
  selectedPrimaryRoles,
  onAreaChange,
  onTeamChange,
}) => {
  const handleProgramAreaChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onAreaChange(event.target.value);
  };

  const handleTeamChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onTeamChange(event.target.value);
  };

  const handleAreaRadioClick = (area: string) => {
    if (selectedArea === area) {
      onAreaChange("");
    } else {
      onAreaChange(area);
    }
  };

  const role: ProgramRole | undefined = selectedPrimaryRoles[0]
    ? program.Roles?.find((role) => role.Name === selectedPrimaryRoles[0])
    : undefined;

  const showNQPAreas = program.ShowNQPAreas || ProgramUtils.showNQPAreas(program?.ID);
  const showProgramAreas = ProgramUtils.showProgramAreas(program?.ID);

  let showTeams;

  if (role) {
    showTeams = role.Teams && role.Teams.length > 0;
  } else {
    showTeams = program.Teams && program.Teams.length > 0;
  }

  let showRoleAreas;

  if (role) {
    showRoleAreas = role?.Areas && role.Areas.length > 0;
  } else {
    showRoleAreas = program.Areas && program.Areas.length > 0;
  }

  if (!showNQPAreas && !showProgramAreas && !showTeams && !showRoleAreas) {
    return null;
  }

  let areasMandatory = false;

  if (showNQPAreas) {
    areasMandatory = ProgramUtils.areNQPAreasMandatory(program?.ID);
  }

  const programAreas = useMemo(() => (program.Areas ? [...program.Areas].sort() : []), [program.Areas]);
  const roleAreas = useMemo(() => (role?.Areas ? [...role.Areas].sort() : []), [role?.Areas]);

  const teams = useMemo(() => {
    if (role) {
      return role.Teams ? [...role.Teams].sort() : [];
    } else {
      return program.Teams ? [...program.Teams].sort() : [];
    }
  }, [role?.Teams, program.Teams]);

  const showTeamsDropdown = role && role.Teams && role.Teams.length > 0;

  return (
    <>
      {/* NQP Areas - Dropdown */}
      {showNQPAreas && (
        <>
          <div className="text-acc-11px font-bold leading-acc-lh text-grey-70 mt-4 mb-1.5">{`Area${areasMandatory ? " (Mandatory)" : ""}`}</div>
          <select
            className="w-full appearance-none bg-gray-200 border border-gray-300 rounded-md py-2 px-3 pr-10 text-grey-80 leading-tight focus:outline-none focus:border-blue-500 truncate custom-select-arrow cursor-pointer"
            onChange={handleProgramAreaChange}
            value={selectedArea}
          >
            <option value="">{"Select an area"}</option>
            {program?.Areas && [...program.Areas].sort().map((area: string) => <option key={area}>{area}</option>)}
          </select>
        </>
      )}

      {/* Program Areas - Radio Buttons */}
      {showProgramAreas && programAreas.length > 0 && (
        <>
          <div className="editRolesHeader mt-6 mb-1.5">{ProgramUtils.getAreaLabel(program?.ID)}</div>
          {programAreas.map((area: string) => (
            <IonItem
              button
              detail={false}
              key={area}
              className="radioItem bg-white cursor-pointer"
              onClick={() => handleAreaRadioClick(area)}
            >
              <div className="w-full flex flex-row items-center justify-between">
                <div className="flex flex-col w-full">
                  <div
                    className={classNames(
                      "text-acc-17px font-acc-400 leading-6 text-grey-90 w-3/4 whitespace-nowrap overflow-hidden overflow-ellipsis",
                      { "font-semibold": area === selectedArea }
                    )}
                  >
                    {area}
                  </div>
                </div>
                <img
                  className="evidencePartsRadioCheckbox"
                  src={area === selectedArea ? Icon_Radio_Active : Icon_Radio_Grey}
                />
              </div>
            </IonItem>
          ))}
        </>
      )}

      {/* Role Areas - Dropdown */}
      {showRoleAreas && roleAreas.length > 0 && (
        <>
          <div className="editRolesHeader mt-6 mb-1.5">{ProgramUtils.getAreaLabel(program?.ID)}</div>
          <select
            className="w-full appearance-none bg-gray-200 border border-gray-300 rounded-md py-2 px-3 pr-10 text-grey-80 leading-tight focus:outline-none focus:border-blue-500 truncate custom-select-arrow cursor-pointer"
            onChange={handleProgramAreaChange}
            value={selectedArea}
          >
            <option value="">{"Select an area"}</option>
            {roleAreas.map((area: string) => (
              <option key={area}>{area}</option>
            ))}
          </select>
        </>
      )}

      {/* Teams  - Dropdown */}
      {showTeams && showTeamsDropdown && (
        <>
          <div className="text-acc-11px font-bold leading-acc-lh text-grey-70 mt-4 mb-1.5">{"Your team"}</div>
          <select
            className="w-full appearance-none bg-gray-200 border border-gray-300 rounded-md py-2 px-3 pr-10 text-grey-80 leading-tight focus:outline-none focus:border-blue-500 truncate custom-select-arrow cursor-pointer"
            onChange={handleTeamChange}
            value={selectedTeam}
          >
            <option value="">{"Select your team"}</option>
            {teams.map((team: string) => (
              <option key={team}>{team}</option>
            ))}
          </select>
        </>
      )}

      {/* Teams  - Radio Buttons */}
      {showTeams && !showTeamsDropdown && (
        <>
          <div className="editRolesHeader mt-4">{"Your team"}</div>
          {teams.map((team: string) => {
            return (
              <IonItem
                button
                detail={false}
                key={team}
                className="detailsRoleCheckboxContainer"
                onClick={() => onTeamChange(team)}
              >
                <div className="evidencePartsCheckboxContainerInner">
                  <div className="programRoleCheckboxContainerInner">
                    <div
                      className={classNames("evidencePartsCheckboxLabel", {
                        evidencePartsCheckboxLabelSelected: selectedTeam === team,
                      })}
                    >
                      {team}
                    </div>
                  </div>
                  <img
                    className="evidencePartsRadioCheckbox"
                    src={selectedTeam === team ? Icon_Radio_Active : Icon_Radio_Grey}
                  />
                </div>
              </IonItem>
            );
          })}
        </>
      )}
    </>
  );
};

export default AreaTeamSelection;
